<template>
  <div class="audio-player" aria-label="Audio Player" role="region">
    <div class="player-controls" role="presentation">
      <div
        v-on:click="seek"
        @keydown="audioSliderNav"
        class="player-progress"
        title="Time played : Total time"
        tabindex="0"
        aria-valuetext="Seek audio bar"
        aria-valuemax="100"
        aria-valuemin="0"
        :aria-valuenow="percentComplete"
        role="slider"
      >
        <div :style="{ width: this.percentComplete + '%' }" class="player-seeker" role="presentation"></div>
      </div>
      <button
        class="play-btn"
        v-bind:class="{ pause: playing }"
        :title="playing ? 'Pause': 'Play'"
        aria-controls="audio"
        @click.stop.prevent="play()"
        role="button"
        tabindex="0"
        :aria-label="playing ? 'Pause': 'Play'" />
    </div>
    <div class="player-details">
      <div class="audio-title">{{ audioTitle }}</div>
      <div class="player-time-current">{{ currentTime }}&nbsp;/&nbsp;{{ timeLeft }}</div>
    </div>
    <audio :loop="loop" id="audio" crossorigin="anonymous" ref="audioNode" :src="audio.job.outputs[0].resultUrl" preload="metadata"></audio>
  </div>
</template>

<script>
export default {
  name: 'AudioPlayer',
  props: {
    audio: {
      type: Object,
      required: true
    },
    loop: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      currentSeconds: 0,
      durationSeconds: 0,
      loaded: false,
      playing: false,
      context: new (window.AudioContext || window.webkitAudioContext)()
    }
  },
  computed: {
    currentTime () {
      let minutes = Math.floor(this.currentSeconds / 60)
      let seconds = this.currentSeconds % 60
      let padding = seconds < 10 ? '0' : ''

      return `${minutes}:${padding}${seconds}`
    },
    timeLeft () {
      let minutes = Math.floor((this.durationSeconds - this.currentSeconds) / 60)
      let seconds = parseInt((this.durationSeconds - this.currentSeconds) % 60)
      let padding = seconds < 10 ? '0' : ''

      return `${minutes}:${padding}${seconds}`
    },
    percentComplete () {
      return parseInt(this.currentSeconds / this.durationSeconds * 100)
    },
    audioTitle () {
      return this.audio.file ? this.audio.file.filename : this.$t('message.listenNow')
    },
    player () {
      return this.$refs.audioNode
    },
    disabledCompresing () {
      if (this.$store.state.college.settings.audio_settings === undefined ||
          this.$store.state.college.settings.audio_settings.disabled_compression === undefined) {
        return false
      } else {
        return this.$store.state.college.settings.audio_settings.disabled_compression
      }
    }
  },
  methods: {
    seek (e) {
      if (e.target.tagName === 'SPAN') {
        return
      }

      const el = e.target.getBoundingClientRect()
      const seekPos = (e.clientX - el.left) / el.width

      this.player.currentTime = parseInt(this.player.duration * seekPos)
    },
    update (e) {
      this.currentSeconds = parseInt(this.player.currentTime)
    },
    setState: function (event) {
      if (event.type === 'play') {
        this.playing = true
      } else {
        this.playing = false
      }
    },
    setDurationTime: function (e) {
      this.durationSeconds = this.player.duration
    },
    play: function () {
      if (this.context) {
        this.context.resume()
      }
      if (this.playing) {
        this.player.pause()
        this.playing = false
        return
      }
      this.player.play()
      this.playing = true
    },
    dbToGain (db) {
      return Math.exp(db * Math.log(10.0) / 20.0)
    },
    initCompression () {
      // let context = new (window.AudioContext || window.webkitAudioContext)()
      let audio = this.$refs.audioNode
      let source = this.context.createMediaElementSource(audio)
      let preGain = this.context.createGain()

      // 20 dB gain
      if (window.webkitAudioContext) {
        preGain.gain.value = this.dbToGain(30)
      } else {
        preGain.gain.value = this.dbToGain(20)
      }

      let limiter = this.context.createDynamicsCompressor()

      limiter.threshold.value = 0.0 // this is the pitfall, leave some headroom
      limiter.knee.value = 0.0 // brute force
      limiter.ratio.value = 20.0 // max compression
      limiter.attack.value = 0.005 // 5ms attack
      limiter.release.value = 0.050 // 50ms release

      source.connect(preGain)
      preGain.connect(limiter)
      limiter.connect(this.context.destination)
    },
    audioSliderNav (e) {
      const isLeft = 37
      const isRight = 39
      const isTop = 38
      const isBottom = 40
      const keyList = [isLeft, isRight, isTop, isBottom]

      if (keyList.indexOf(e.keyCode) >= 0) {
        let percentage
        switch (e.keyCode) {
          case isLeft:
            percentage = this.percentComplete - 1
            break
          case isRight:
            percentage = this.percentComplete + 1
            break
          case isTop:
            percentage = this.percentComplete + 10
            break
          case isBottom:
            percentage = this.percentComplete - 10
            break
          default:
            break
        }

        if (percentage > 100) {
          percentage = 100
        } else if (percentage < 0) {
          percentage = 0
        }

        if (e.keyCode === isRight || e.keyCode === isTop) {
          this.player.currentTime = Math.ceil(this.player.duration * (percentage / 100))
        } else {
          this.player.currentTime = Math.floor(this.player.duration * (percentage / 100))
        }
      }
    }
  },
  created () {
    this.innerLoop = this.loop
  },
  mounted () {
    this.player.addEventListener('timeupdate', this.update)
    this.player.addEventListener('pause', this.setState)
    this.player.addEventListener('play', this.setState)
    this.player.addEventListener('loadedmetadata', this.setDurationTime)

    const userAgent = navigator.userAgent || navigator.vendor || window.opera

    if (!/iPad|iPhone|iPod/.test(userAgent) && !this.disabledCompresing) {
      this.initCompression()
    }
  },
  beforeDestroy () {
    this.player.removeEventListener('timeupdate', this.update)
    this.player.removeEventListener('pause', this.setState)
    this.player.removeEventListener('play', this.setState)
    this.player.removeEventListener('loadedmetadata', this.setDurationTime)
  }
}
</script>
